/* eslint-disable @typescript-eslint/explicit-function-return-type */
import { type UnknownAction, combineReducers, configureStore, type ThunkAction, createAsyncThunk } from '@reduxjs/toolkit';
import { useDispatch, useSelector } from 'react-redux';
import { resourcesApi } from 'src/resources';
import { portalLanguages } from 'src/util/portalLanguage-utils';

import pendingChanges from '@helsenorge/core-framework/pending-changes/pending-changes-state';
import prosesshjelp from '@helsenorge/core-framework/prosesshjelp/store';
import type LanguageLocales from '@helsenorge/core-utils/constants/languages';
import { tjenesteTeksterApi } from '@helsenorge/framework-utils/cms-tjeneste-tekster';
import type { ProxyErrorResponse } from '@helsenorge/framework-utils/hn-proxy-service';
import { createLanguageReducer } from '@helsenorge/framework-utils/language';
import { form } from '@helsenorge/refero';

import microweb, { type MicrowebSliceState, initialState as microwebInitialState } from './microwebSlice';
import ui from './uiSlice';

const rootReducer = combineReducers({
  language: createLanguageReducer(portalLanguages.filter(lang => lang !== '') as LanguageLocales[]),
  [resourcesApi.reducerPath]: resourcesApi.reducer,
  prosesshjelp,
  microweb,
  refero: combineReducers({
    ui,
    form,
  }),
  [tjenesteTeksterApi.reducerPath]: tjenesteTeksterApi.reducer,
  pendingChanges,
});

export const store = (microwebPreLoad?: Partial<MicrowebSliceState>) => {
  return configureStore({
    reducer: rootReducer,
    preloadedState: {
      ...rootReducer,
      microweb: {
        ...microwebInitialState,
        ...microwebPreLoad,
      },
    },
    middleware: getDefaultMiddleware => getDefaultMiddleware().concat(tjenesteTeksterApi.middleware).concat(resourcesApi.middleware),
  });
};

export type RootState = ReturnType<typeof rootReducer>;
export type AppStore = ReturnType<typeof store>;
export type AppDispatch = AppStore['dispatch'];
export const createAppAsyncThunk = createAsyncThunk.withTypes<{
  state: RootState;
  dispatch: AppDispatch;
  rejectValue: string | ProxyErrorResponse;
  extra: { s: string; n: number };
}>();

export type AppThunk<ReturnType = void> = ThunkAction<ReturnType, RootState, unknown, UnknownAction>;
export const useAppDispatch = () => useDispatch<AppDispatch>();
export function useAppSelector<TSelected = unknown>(selector: (state: RootState) => TSelected): TSelected {
  return useSelector(selector);
}
export default rootReducer;
